/**
 * Spaces out gallery reveals so images never appear faster than a set interval.
 *
 * Generation is parallel and its completion order is not the order we want to
 * show things in: with `parallel_generations` raised, portraits land in bursts
 * and the gallery snaps several tiles at once, which reads as a glitch rather
 * than as results arriving. Pacing is deliberately decoupled from generation —
 * requests keep going out as fast as the backend allows, and only the *display*
 * is rate-limited.
 *
 * FIFO: reveals fire in the order they were enqueued, so pacing never reorders
 * what generation produced.
 */

/**
 * 5s, and the resulting tail is intentional — not an oversight.
 *
 * The gap applies between *every* pair, so a full set keeps arriving well after
 * generation finishes: a vertical kiosk with one face renders 8 portraits, so
 * the last one lands ~35s after the first. That steady drip is the point — a
 * paced reveal reads as results arriving, where a burst reads as a glitch.
 * Shortening this to "finish sooner" trades the whole reason the pacer exists.
 */
export const DEFAULT_MIN_REVEAL_INTERVAL_MS = 5000;

export interface RevealPacerOptions {
  /** Minimum gap between two reveals. */
  intervalMs?: number;
  /** Clock, injectable so tests don't depend on wall time. */
  now?: () => number;
  schedule?: (fn: () => void, ms: number) => unknown;
  cancelScheduled?: (handle: never) => void;
  /**
   * A throwing reveal must not stall the queue; the remaining items still
   * drain and the error is reported here.
   */
  onError?: (error: unknown) => void;
}

export interface RevealPacer {
  /** Enqueue a reveal. Runs synchronously when the gap has already elapsed. */
  reveal(fn: () => void): void;
  /**
   * Drop everything pending and stop the timer. Called on session reset —
   * without it a queued reveal fires into a cleared session, and the timer
   * outlives the session that created it.
   */
  cancel(): void;
  readonly pendingCount: number;
}

export function createRevealPacer(
  options: RevealPacerOptions = {}
): RevealPacer {
  const {
    intervalMs = DEFAULT_MIN_REVEAL_INTERVAL_MS,
    now = () => Date.now(),
    schedule = setTimeout as unknown as NonNullable<
      RevealPacerOptions['schedule']
    >,
    cancelScheduled = clearTimeout as unknown as NonNullable<
      RevealPacerOptions['cancelScheduled']
    >,
    onError = () => {},
  } = options;

  const queue: Array<() => void> = [];
  // null (not 0) so the very first reveal is never held back — the gap is
  // between images, and an empty gallery should fill immediately.
  let lastRevealAt: number | null = null;
  let timer: unknown = null;

  function pump(): void {
    if (timer !== null) return;
    // Loop rather than recurse: with intervalMs=0 the whole queue drains in one
    // pass, and recursion there would grow the stack per pending image.
    while (queue.length > 0) {
      const wait =
        lastRevealAt === null
          ? 0
          : Math.max(0, lastRevealAt + intervalMs - now());
      if (wait > 0) {
        timer = schedule(() => {
          timer = null;
          pump();
        }, wait);
        return;
      }
      lastRevealAt = now();
      const revealNext = queue.shift();
      try {
        revealNext?.();
      } catch (error) {
        onError(error);
      }
    }
  }

  return {
    reveal(fn: () => void): void {
      queue.push(fn);
      pump();
    },
    cancel(): void {
      if (timer !== null) {
        cancelScheduled(timer as never);
        timer = null;
      }
      queue.length = 0;
      lastRevealAt = null;
    },
    get pendingCount(): number {
      return queue.length;
    },
  };
}
